import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import type { Condition } from "@/types";

import type { InvocationGroupBy, InvocationStatus } from "../ActionRecord";

export interface UseInvocationBreakdownOptions {
  /** Count invocations whose current status is one of these. */
  statuses: InvocationStatus[];
  groupBy: InvocationGroupBy[];
  condition?: Condition;
  limit?: number;
  refetchInterval?: number | false;
}

/** The current org's invocations in some statuses right now, grouped, largest group first. */
export function useInvocationBreakdown({
  statuses,
  groupBy,
  condition,
  limit,
  refetchInterval = false,
}: UseInvocationBreakdownOptions) {
  const { actionService } = useDomainServices();
  const { currentOrganization } = useAuth();

  return useQuery({
    queryKey: [
      "invocationBreakdown",
      currentOrganization?.org_id,
      statuses,
      groupBy,
      condition ?? null,
      limit ?? null,
    ],
    queryFn: async ({ signal }) => {
      if (currentOrganization === null) {
        throw new Error("Requires login");
      }

      return actionService.getInvocationBreakdown(
        currentOrganization.org_id,
        { statuses, group_by: groupBy, condition, limit },
        { abortSignal: signal },
      );
    },
    enabled: currentOrganization !== null,
    refetchInterval,
    // The app default is five minutes, which would let a count labelled "right now" be
    // five minutes old with nothing on screen saying so — and with `refetchInterval`
    // off, nothing else would force a fetch either. Zero makes a remount or a window
    // focus refetch; `keepPreviousData` means the old counts stay up while it does.
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}
